import React from 'react';

// 🔥 [2026-04-23] React lazy import 가 stale 번들 chunk 를 fetch 하다 404 / network 실패하면
// 던지는 에러 패턴. Vite/webpack 모두 message 또는 name 으로 식별 가능.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || '');
  const msg = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg)
  );
}

const RELOAD_FLAG_KEY = '__chunk_reload_attempt__';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo);
    }
    // 🔥 [2026-04-23] stale 번들 chunk 404 자동 복구.
    // 새 빌드 후 옛 index.html 캐시를 가진 브라우저가 옛 chunk hash 를 fetch → 없음 (404).
    // 이때 1회 자동 새로고침해서 새 index.html + 새 chunk 로 갱신.
    // 무한 새로고침 방지: sessionStorage 플래그로 1회만 시도.
    if (isChunkLoadError(error)) {
      try {
        const already = sessionStorage.getItem(RELOAD_FLAG_KEY);
        if (!already) {
          sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch (_) {
        // sessionStorage 사용 불가 환경 (private mode 등) 은 그냥 fallback UI 노출
      }
    }
  }

  handleReload = () => {
    try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch (_) {}
    window.location.reload();
  };

  handleGoHome = () => {
    try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch (_) {}
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.icon}>!</div>
          <h2 style={styles.title}>문제가 발생했습니다</h2>
          <p style={styles.message}>
            예기치 않은 오류가 발생했습니다. 아래 버튼을 눌러 다시 시도해 주세요.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre style={styles.errorDetail}>
              {this.state.error.message}
            </pre>
          )}
          <div style={styles.buttonGroup}>
            <button style={styles.primaryButton} onClick={this.handleReload}>
              새로고침
            </button>
            <button style={styles.secondaryButton} onClick={this.handleGoHome}>
              홈으로 이동
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--app-splash-bg, var(--color-bg-primary, #F0F4FA))',
    padding: '24px',
    textAlign: 'center',
  },
  icon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-status-error, #EF4444)',
    color: 'var(--color-text-primary, #0F172A)',
    fontSize: '28px',
    fontWeight: '700',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    color: '#FFFFFF',
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 12px 0',
  },
  message: {
    color: 'var(--color-text-secondary, #475569)',
    fontSize: '14px',
    lineHeight: '1.6',
    maxWidth: '320px',
    margin: '0 0 24px 0',
  },
  errorDetail: {
    color: 'var(--color-status-error, #EF4444)',
    fontSize: '12px',
    backgroundColor: 'var(--color-bg-secondary, #FFFFFF)',
    padding: '12px 16px',
    borderRadius: '8px',
    maxWidth: '400px',
    overflow: 'auto',
    margin: '0 0 24px 0',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--color-secondary, #fbba21)',
    color: 'var(--color-primary-dark, #031530)',
    border: 'none',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary, #475569)',
    border: '1px solid var(--color-border-primary, #E2E8F0)',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default ErrorBoundary;
